/**
 * MyActivityDrawer — anchor-themed activity panel.
 *
 * Right-edge slide drawer that surfaces the four player-state cards in
 * the design-system visual language:
 *
 *   1. QuestProgressCard       — Quest Progression
 *   2. StatsCard               — My Stats
 *   3. LastSessionCard         — Last Session
 *   4. TransactionHistoryCard  — Recent Rewards
 *
 * Drawer shell owns chrome only — the cards themselves are unchanged so
 * any in-place upgrades to their data wiring continue to flow through.
 * Each card sits inside a labeled section block with a mono eyebrow.
 */

import React, {ReactElement, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import classNames from "classnames";
import {Token} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import {
    decrementModalCount,
    incrementModalCount,
    toggleHideBottomNav,
} from "@/redux/meta/metaActions";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {QuestProgressCard} from "@/components/play/cards/QuestProgressCard";
import StatsCard from "@/components/play/cards/StatsCard";
import {LastSessionCard} from "@/components/play/cards/LastSessionCard";
import TransactionHistoryCard from "@/components/play/cards/TransactionHistoryCard";
import "./MyActivityDrawer.scss";

interface Props {
    show: boolean;
    toggle: () => void;
}

function MyActivityDrawer(props: Props): ReactElement {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken) as Token | undefined;
    const {data: currentUser} = useGetUserProfile(fullToken!);

    useEffect(() => {
        if (props.show) {
            dispatch(incrementModalCount());
            dispatch(toggleHideBottomNav(true));
        } else {
            dispatch(toggleHideBottomNav(false));
        }

        return () => {
            dispatch(toggleHideBottomNav(false));
            dispatch(decrementModalCount());
        };
    }, [props.show]);

    function handleClose(e: React.MouseEvent | React.TouchEvent): void {
        e.stopPropagation();
        props.toggle();
    }

    const userId = currentUser?.user?.id;

    return (
        <>
            <div
                className={classNames("my-activity-drawer__overlay", {
                    "my-activity-drawer__overlay--visible": props.show,
                })}
                onClick={handleClose}
                role="presentation"
            />
            <aside
                className={classNames("my-activity-drawer", {
                    "my-activity-drawer--open": props.show,
                })}
                role="dialog"
                aria-label="My Activity"
                aria-hidden={!props.show}
            >
                <header className="ma-hd">
                    <div className="ma-hd-row">
                        <div className="ma-hd-eyebrow">PLAYER OPS</div>
                        <button
                            type="button"
                            className="ma-hd-close"
                            onClick={handleClose}
                            aria-label="Close my activity drawer"
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <h2 className="ma-hd-title">MY <span>ACTIVITY</span></h2>
                    <p className="ma-hd-sub">Quests in motion, stats earned, recent rewards — all in one feed.</p>
                </header>

                {fullToken && userId
                    ? <div className="ma-content">
                        <section className="ma-section">
                            <header className="ma-section-hd">
                                <div className="ma-section-rail" aria-hidden="true" />
                                <span className="ma-section-eyebrow">PROGRESSION</span>
                                <h3 className="ma-section-title">Quest Progression</h3>
                            </header>
                            <div className="ma-section-body">
                                <QuestProgressCard compact={true} defaultExpanded={true} />
                            </div>
                        </section>

                        <section className="ma-section">
                            <header className="ma-section-hd">
                                <div className="ma-section-rail" aria-hidden="true" />
                                <span className="ma-section-eyebrow">PROFILE</span>
                                <h3 className="ma-section-title">My Stats</h3>
                            </header>
                            <div className="ma-section-body">
                                <StatsCard fullToken={fullToken} userId={userId} defaultExpanded={true} />
                            </div>
                        </section>

                        <section className="ma-section">
                            <header className="ma-section-hd">
                                <div className="ma-section-rail" aria-hidden="true" />
                                <span className="ma-section-eyebrow">CHECK-IN</span>
                                <h3 className="ma-section-title">Last Session</h3>
                            </header>
                            <div className="ma-section-body">
                                <LastSessionCard defaultExpanded={true} />
                            </div>
                        </section>

                        <section className="ma-section">
                            <header className="ma-section-hd">
                                <div className="ma-section-rail" aria-hidden="true" />
                                <span className="ma-section-eyebrow">LEDGER</span>
                                <h3 className="ma-section-title">Recent Rewards</h3>
                            </header>
                            <div className="ma-section-body">
                                <TransactionHistoryCard fullToken={fullToken} userId={userId} defaultExpanded={true} />
                            </div>
                        </section>
                    </div>
                    : <div className="ma-empty" role="status">
                        <p className="ma-empty-title">Sign in to view your activity</p>
                        <p className="ma-empty-sub">Quest progression, stats, and reward history land here once you authenticate.</p>
                    </div>
                }
            </aside>
        </>
    );
}

export default MyActivityDrawer;
